import React, { useState } from 'react';
import { AlertTriangle, ArrowLeft, Check, ChevronDown, CircleAlert, CircleX, Download, Eye, FileText, Globe2, Info, Leaf, Package, Pencil, Plus, Settings, ThumbsDown, ThumbsUp, UploadCloud, X, Image as ImageIcon } from 'lucide-react';
import { Flag } from 'lucide-react';
import { ReadinessRing } from '../ui/ReadinessRing';
import { Tooltip } from '../ui/Tooltip';
import { RegulationLink } from '../ui/RegulationLink';
type PackFormat = 'carton' | 'sachet-individual' | 'sachet-not-individual';
type ReportTab = 'overview' | 'findings' | 'product-data' | 'documents';
type RequirementStatus = 'pass' | 'fail' | 'voluntary';
type FindingStatus = 'Pass' | 'Fail' | 'Not applicable';
export type MarketVariant = 'AU' | 'SG';
type Requirement = {
  name: string;
  status: RequirementStatus;
  note?: string;
};
type Finding = {
  name: string;
  status: FindingStatus;
  detail: string;
  severity?: 'High' | 'Medium';
  reference?: string;
  recommendation?: string;
  infoRequired?: boolean;
  comparison?: boolean;
};
type FindingSection = {
  title: string;
  subtitle?: string;
  issues: string;
  severity: string[];
  passRatio: string;
  notApplicable?: string;
  linkToFormula?: boolean;
  findings: Finding[];
};
const formats: {
  id: PackFormat;
  label: string;
}[] = [{
  id: 'carton',
  label: 'Outer carton'
}, {
  id: 'sachet-individual',
  label: 'Sachet (if sold individually)'
}, {
  id: 'sachet-not-individual',
  label: 'Sachet (not for individual sale)'
}];
const reportTabs: {
  id: ReportTab;
  label: string;
}[] = [{
  id: 'overview',
  label: 'Overview'
}, {
  id: 'findings',
  label: 'Findings'
}, {
  id: 'product-data',
  label: 'Product data'
}, {
  id: 'documents',
  label: 'Documents'
}];
const cartonRequirements: Requirement[] = [{
  name: 'Brand name',
  status: 'pass'
}, {
  name: 'Product name',
  status: 'pass'
}, {
  name: 'Net quantity declaration',
  status: 'pass'
}, {
  name: 'Storage conditions',
  status: 'pass'
}, {
  name: 'Directions for use present',
  status: 'pass'
}, {
  name: 'Country of origin',
  status: 'pass'
}, {
  name: 'Batch or lot identification',
  status: 'fail',
  note: 'Mandatory on all formats'
}, {
  name: 'Expiry date',
  status: 'fail',
  note: 'Mandatory on all formats'
}];
const nonIndividualRequirements: Requirement[] = [{
  name: 'Brand name',
  status: 'voluntary'
}, {
  name: 'Product name',
  status: 'voluntary'
}, {
  name: 'Net quantity declaration',
  status: 'voluntary'
}, {
  name: 'Storage conditions',
  status: 'voluntary'
}, {
  name: 'Directions for use present',
  status: 'voluntary'
}, {
  name: 'Country of origin',
  status: 'voluntary'
}, {
  name: 'Batch or lot identification',
  status: 'fail',
  note: 'Mandatory on all formats'
}, {
  name: 'Expiry date',
  status: 'fail',
  note: 'Mandatory on all formats'
}, {
  name: "'Not labelled for individual sale' statement present",
  status: 'pass'
}, {
  name: "'See carton for ingredients and other product information' statement present",
  status: 'pass'
}];
const TGO92 = 'TGO 92';
const RASML = 'RASML — Medicines Advisory Statements Specification';
const PID = 'Therapeutic Goods (Permissible Indications) Determination';
const mandatoryFindings: Finding[] = [{
  name: 'AUST L number displayed',
  status: 'Fail',
  severity: 'High',
  detail: 'No AUST L number appears on the artwork.',
  reference: 'Therapeutic Goods Regulations 1990 / TGO 92'
}, {
  name: 'Australian sponsor name and address',
  status: 'Fail',
  severity: 'High',
  detail: "The artwork declares 'Manufactured for Zest Packaged Foods, LLC, Westlake Village, CA 91361'. No Australian sponsor is identified.",
  reference: TGO92
}, {
  name: 'Batch or lot identification',
  status: 'Fail',
  severity: 'High',
  detail: 'No batch or lot number found on the artwork.',
  reference: TGO92
}, {
  name: 'Expiry date',
  status: 'Fail',
  severity: 'High',
  detail: 'No expiry date or date-marking field found on the artwork.',
  reference: TGO92
}, {
  name: 'Legal name / product designation',
  status: 'Fail',
  severity: 'High',
  detail: "The descriptor reads 'DIETARY SUPPLEMENT MADE WITH FRUIT JUICE POWDER AND ADDED VITAMINS A & C AND ZINC'. 'Dietary supplement' is a United States classification with no Australian equivalent.",
  reference: TGO92
}, {
  name: 'Actives declaration format',
  status: 'Fail',
  severity: 'High',
  detail: 'Actives are presented in a United States Supplement Facts panel. A listed medicine must declare the name and quantity of each active ingredient in the form required by TGO 92.',
  reference: TGO92
}, {
  name: 'Units of measurement',
  status: 'Fail',
  severity: 'Medium',
  detail: 'Net quantity and directions use ounces and fluid ounces. Metric units are required.',
  reference: TGO92
}, {
  name: 'Australian contact details',
  status: 'Fail',
  severity: 'Medium',
  detail: 'The only contact is a United States toll-free number, 1-800-232-8888.',
  reference: TGO92
}, {
  name: 'Country of origin declared',
  status: 'Pass',
  detail: 'Product of Taiwan',
  reference: TGO92
}, {
  name: 'Storage conditions',
  status: 'Pass',
  detail: 'Store in a cool, dry place. Keep out of reach of children.',
  reference: TGO92
}, {
  name: 'Net quantity stated in metric alongside imperial',
  status: 'Pass',
  detail: '6 g per sachet, 90 g per carton',
  reference: TGO92
}, {
  name: 'Directions for use present',
  status: 'Pass',
  detail: 'Directions for use are present on the supplied artwork.',
  reference: TGO92
}];
const formulatedIngredients: {
  name: string;
  amount: string;
  missing?: boolean;
}[] = [{
  name: 'Orange fruit juice powder',
  amount: '51.133%'
}, {
  name: 'Soluble corn fibre',
  amount: '34.291%'
}, {
  name: 'Vitamin C (ascorbic acid)',
  amount: '11.667%',
  missing: true
}, {
  name: 'Silicon dioxide',
  amount: '1.000%'
}, {
  name: 'Zinc (zinc sulfate)',
  amount: '0.667%',
  missing: true
}, {
  name: 'Stevia extract',
  amount: '0.500%'
}, {
  name: 'Vitamin A (retinyl acetate)',
  amount: '0.400%',
  missing: true
}, {
  name: 'Beta carotene',
  amount: '0.325%'
}];
const declaredIngredients = ['Orange juice powder', 'Soluble corn fibre', 'Silicon dioxide (anticaking)', 'Stevia extract', 'Beta carotene (colour)'];
const ingredientFindings: Finding[] = [{
  name: 'Actives declared in the ingredient statement',
  status: 'Fail',
  severity: 'High',
  comparison: true,
  detail: "Vitamin A, vitamin C and zinc appear in the Supplement Facts panel but not in the ingredient statement. The artwork splits actives from 'other ingredients' following United States convention. Declare all ingredients in a single statement.",
  reference: TGO92
}, {
  name: 'Characterising ingredient percentage',
  status: 'Fail',
  severity: 'Medium',
  detail: 'Orange fruit juice powder is 51.1% of the formulation and is named in the product name, making it a characterising ingredient. No percentage is declared on the artwork.',
  reference: TGO92
}, {
  name: 'Flavour component identified',
  status: 'Fail',
  severity: 'Medium',
  detail: "The formulation specifies 'Natural Orange Flavor (Orange 820163 TRE)'. The artwork declares only 'NATURAL FLAVOR'. Confirm whether the supplier blend requires more specific identification.",
  reference: TGO92
}, {
  name: 'Ingredient order matches descending formulation weight',
  status: 'Pass',
  detail: 'On-pack order matches the formulation: orange fruit juice powder 51.1%, soluble corn fibre 34.3%, silicon dioxide 1.0%, stevia extract 0.5%, beta carotene 0.3%.',
  reference: TGO92
}, {
  name: 'All formulated excipients appear on the label',
  status: 'Pass',
  detail: 'Five of five excipients are declared.',
  reference: TGO92
}, {
  name: 'No on-pack ingredient absent from the formulation',
  status: 'Pass',
  detail: 'No ingredient is declared that does not appear in the formulation.',
  reference: TGO92
}];
const legacyIngredientFindings: Finding[] = [{
  name: 'Vitamin A exceeds the advisory statement threshold',
  status: 'Fail',
  severity: 'High',
  detail: 'The product provides 3,600 µg retinol equivalents (12,000 IU) per sachet, above the 3,000 µg RE (10,000 IU) level at which a mandatory advisory statement applies.',
  reference: 'RASML'
}, {
  name: 'Resistant maltodextrin unresolved',
  status: 'Fail',
  severity: 'High',
  detail: 'Soluble corn fibre (Fibersol-2, a corn-derived resistant maltodextrin) is present at 34.3% directly plus 35.7% of the compound; could not be resolved against the Permissible Ingredients Determination.',
  reference: 'Therapeutic Goods (Permissible Ingredients) Determination'
}, {
  name: 'Fruit juice concentrates unresolved',
  status: 'Fail',
  severity: 'Medium',
  detail: 'Orange juice concentrate and mandarin orange juice concentrate could not be resolved against the Permissible Ingredients Determination.',
  reference: 'Therapeutic Goods (Permissible Ingredients) Determination'
}, {
  name: 'Vitamin C overage not justified',
  status: 'Fail',
  severity: 'Medium',
  detail: '700 mg formulated against 550 mg declared — approximately 27% overage.',
  reference: 'Default standard (BP)'
}, {
  name: 'Zinc within permitted daily limit',
  status: 'Pass',
  detail: '40 mg zinc sulfate provides 15 mg elemental zinc, within the 25 mg limit',
  reference: 'Therapeutic Goods (Permissible Ingredients) Determination'
}, {
  name: 'Ascorbic acid permitted',
  status: 'Pass',
  detail: 'Ascorbic acid resolves to a permitted active ingredient entry.',
  reference: 'Therapeutic Goods (Permissible Ingredients) Determination'
}, {
  name: 'Silicon dioxide permitted',
  status: 'Pass',
  detail: 'Silicon dioxide resolves to a permitted excipient entry.',
  reference: 'Therapeutic Goods (Permissible Ingredients) Determination'
}, {
  name: 'Steviol glycosides permitted',
  status: 'Pass',
  detail: 'Steviol glycosides resolve to a permitted excipient entry.',
  reference: 'Therapeutic Goods (Permissible Ingredients) Determination'
}, {
  name: 'Beta carotene permitted as a colour',
  status: 'Pass',
  detail: 'Beta carotene resolves to a permitted colouring entry.',
  reference: 'Therapeutic Goods (Permissible Ingredients) Determination'
}];
const advisoryFindings: Finding[] = [{
  name: 'Vitamin A advisory statement absent',
  status: 'Fail',
  severity: 'High',
  detail: 'The product contains 600 µg retinol equivalents per gram, far above the 33 µg per gram concentration below which advisory statement requirements do not apply, and the daily dose exceeds 10,000 IU. No advisory statement appears on any face of the artwork.',
  reference: RASML
}, {
  name: 'Advisory statement screening not completed for remaining actives',
  status: 'Fail',
  severity: 'Medium',
  detail: 'Advisory statement screening has not been completed for the remaining actives in the formulation.',
  reference: RASML
}];
const claimsFindings: Finding[] = [{
  name: 'Indications not drawn from the Permissible Indications Determination',
  status: 'Fail',
  severity: 'High',
  detail: 'Indications on the artwork are free-text and do not match listed indications verbatim.',
  reference: PID
}, {
  name: 'Food-style directions of use',
  status: 'Fail',
  severity: 'High',
  detail: "'Add to any of your favorite smoothies, yogurt or oatmeal'",
  reference: TGO92
}, {
  name: 'United States DSHEA disclaimer present',
  status: 'Fail',
  severity: 'Medium',
  detail: 'The DSHEA disclaimer has no standing in Australia and should be removed.',
  reference: 'Therapeutic Goods Advertising Code'
}, {
  name: 'Disparaging comparison',
  status: 'Fail',
  severity: 'Medium',
  detail: "'Instead of the usual synthetic fillers…'",
  reference: 'Therapeutic Goods Advertising Code'
}, {
  name: 'Guarantee claim',
  status: 'Fail',
  severity: 'Medium',
  detail: "'SATISFACTION GUARANTEED'",
  reference: 'Therapeutic Goods Advertising Code'
}, {
  name: 'Percent Daily Value figures used as claims',
  status: 'Fail',
  severity: 'Medium',
  detail: 'Percent Daily Value is a United States construct and is used on the artwork as a claim.',
  reference: TGO92
}, {
  name: 'No disease or therapeutic claims detected',
  status: 'Pass',
  detail: 'No prohibited disease or therapeutic claims were detected on the artwork.',
  reference: 'Therapeutic Goods Advertising Code'
}, {
  name: 'No restricted representations detected',
  status: 'Pass',
  detail: 'No restricted representations were detected on the artwork.',
  reference: 'Therapeutic Goods Advertising Code'
}, {
  name: 'Website and social handle disclosed',
  status: 'Pass',
  detail: 'Sponsor website and social handle are disclosed on the artwork.',
  reference: 'Therapeutic Goods Advertising Code'
}, {
  name: 'Dietary preference claims unrestricted',
  status: 'Pass',
  detail: 'Vegan and gluten-free statements are unrestricted voluntary claims.',
  reference: 'Therapeutic Goods Advertising Code'
}, {
  name: 'Production method claims unrestricted',
  status: 'Pass',
  detail: 'Non-GMO and no-preservative statements are unrestricted voluntary claims.',
  reference: 'Therapeutic Goods Advertising Code'
}];
const panelFindings: Finding[] = [{
  name: 'United States Supplement Facts panel format used',
  status: 'Fail',
  severity: 'High',
  detail: 'The actives panel follows the United States Supplement Facts format rather than the Australian requirement.',
  reference: TGO92
}, {
  name: "'Percent Daily Values are based on a 2,000 calorie diet' footnote",
  status: 'Fail',
  severity: 'Medium',
  detail: 'This footnote is a United States construct and has no Australian equivalent.',
  reference: TGO92
}, {
  name: 'Total carbohydrate declaration inconsistent with formulation',
  status: 'Fail',
  severity: 'Medium',
  detail: '6 g declared in a 6 g sachet, but approximately 0.9 g of the sachet is non-carbohydrate material.',
  reference: TGO92
}, {
  name: 'Dietary fibre declaration inconsistent with formulation',
  status: 'Fail',
  severity: 'Medium',
  detail: '2 g declared against approximately 3.15 g resistant maltodextrin in the formulation.',
  reference: TGO92
}, {
  name: 'Each active named with its chemical form',
  status: 'Pass',
  detail: 'Each active ingredient is named together with its chemical form.',
  reference: TGO92
}, {
  name: 'Serving size and servings per container stated',
  status: 'Pass',
  detail: 'Serving size and servings per container are both stated on the artwork.',
  reference: TGO92
}];
const otherFindings: Finding[] = [{
  name: 'No allergen or cross-contact statement present',
  status: 'Fail',
  severity: 'Medium',
  detail: 'The artwork carries no allergen or cross-contact statement.',
  reference: TGO92
}, {
  name: 'Barcode area present',
  status: 'Pass',
  detail: 'A barcode area is present on the artwork.',
  reference: TGO92
}, {
  name: "'Keep out of reach of children' present",
  status: 'Pass',
  detail: "'Keep out of reach of children' appears on the artwork.",
  reference: TGO92
}, {
  name: 'No scheduled ingredients detected (Poisons Standard)',
  status: 'Pass',
  detail: 'No scheduled ingredients were detected against the Poisons Standard.',
  reference: 'Poisons Standard'
}];
const findingSections: FindingSection[] = [{
  title: 'Mandatory Labelling Requirements',
  issues: '8 issues',
  severity: ['6 HIGH', '2 MEDIUM'],
  passRatio: '4/12 PASS',
  findings: mandatoryFindings
}, {
  title: 'Ingredient Compliance',
  subtitle: 'Does the label declare what was formulated?',
  issues: '3 issues',
  severity: ['1 HIGH', '2 MEDIUM'],
  passRatio: '3/6 PASS',
  linkToFormula: true,
  findings: ingredientFindings
}, {
  title: 'Advisory & Warning Statements',
  issues: '2 issues',
  severity: ['1 HIGH', '1 MEDIUM'],
  passRatio: '0/2 PASS',
  findings: advisoryFindings
}, {
  title: 'Claims & Presentation',
  issues: '6 issues',
  severity: ['2 HIGH', '4 MEDIUM'],
  passRatio: '5/11 PASS',
  findings: claimsFindings
}, {
  title: 'Nutrition / Actives Panel',
  issues: '4 issues',
  severity: ['1 HIGH', '3 MEDIUM'],
  passRatio: '2/6 PASS',
  findings: panelFindings
}, {
  title: 'Other Verifications',
  issues: '1 issue',
  severity: ['1 MEDIUM'],
  passRatio: '3/4 PASS',
  findings: otherFindings
}];
const HSA = 'Singapore Health Supplement Guidelines';
const sgMandatoryFindings: Finding[] = [{
  name: 'Name and address of local importer',
  status: 'Fail',
  severity: 'High',
  detail: 'The label declares the overseas manufacturer but no local importer. Include the name and address of the Singapore importer.',
  reference: HSA
}, {
  name: 'Lot identification',
  status: 'Fail',
  severity: 'High',
  detail: 'No lot or batch number found on the artwork.',
  reference: HSA
}, {
  name: 'Expiry date',
  status: 'Fail',
  severity: 'High',
  detail: 'No expiry date found on the artwork.',
  reference: HSA
}, {
  name: 'Legal name',
  status: 'Fail',
  severity: 'Medium',
  detail: "The legal name highlights 'Fruit Juice Powder'. As this product is intended as a health supplement, wording that refers to food should not be used on the label, to avoid confusion between food and health supplement products.",
  recommendation: "Amend to 'Vitamin A & C and Zinc Dietary Supplement'.",
  reference: HSA
}, {
  name: 'Net content',
  status: 'Fail',
  severity: 'Medium',
  detail: "Change 'packets' to 'sachets' to be consistent with the recognised single-unit pharmaceutical dosage form. Net weight is not necessary.",
  recommendation: "Use 'Pack size: 15 × 6 g sachets'.",
  reference: HSA
}, {
  name: 'Brand name',
  status: 'Pass',
  detail: 'Brand name appears on the front face of the artwork.',
  reference: HSA
}, {
  name: 'Product name',
  status: 'Pass',
  detail: 'Product name appears on the front face of the artwork.',
  reference: HSA
}, {
  name: 'Ingredients list',
  status: 'Pass',
  detail: 'Only active ingredients need to be labelled.',
  reference: HSA
}, {
  name: 'Manufacturer and country of origin',
  status: 'Pass',
  detail: 'Manufacturer and country of origin are declared on the artwork.',
  reference: HSA
}, {
  name: 'Storage conditions',
  status: 'Pass',
  detail: 'Store in a cool, dry place. Keep out of reach of children.',
  reference: HSA
}];
const sgActivesFindings: Finding[] = [{
  name: 'Nutrition Information Panel present',
  status: 'Fail',
  severity: 'Medium',
  detail: 'A Nutrition Information Panel is not required for a health supplement. Under the Singapore Health Supplement Guidelines only the name and quantity of the actives need be declared.',
  recommendation: 'Replace with: Active ingredients: Vitamin A (as retinyl acetate) 3,600 µg; Vitamin C (as ascorbic acid) 550 mg; Zinc (as zinc sulfate) 15 mg.',
  reference: HSA
}, {
  name: 'Percent Daily Value footnote',
  status: 'Fail',
  severity: 'Medium',
  detail: 'The Percent Daily Value footnote is a United States construct and has no standing in Singapore.',
  reference: HSA
}, {
  name: 'Actives named with chemical form',
  status: 'Pass',
  detail: 'Each active ingredient is named together with its chemical form.',
  reference: HSA
}, {
  name: 'Serving size stated',
  status: 'Pass',
  detail: 'Serving size is stated on the artwork.',
  reference: HSA
}];
const sgClaimsFindings: Finding[] = [{
  name: 'Fruit emphasis in health claim',
  status: 'Fail',
  severity: 'Medium',
  detail: 'It is advisable to avoid over-emphasising the fruit content, to avoid confusion between food and health supplement products.',
  recommendation: "Delete 'Orange' from the claim line.",
  reference: HSA
}, {
  name: 'Food-style directions of use',
  status: 'Fail',
  severity: 'Medium',
  detail: "'Add to any of your favourite smoothies, yogurt or oatmeal' presents the product as a food rather than a health supplement.",
  reference: HSA
}, {
  name: 'Health claims',
  status: 'Pass',
  detail: 'Structural claims and health claims that suggest maintenance and support of good health, such as immunity, are acceptable for health supplements.',
  reference: HSA
}, {
  name: 'Structure-function claims',
  status: 'Pass',
  detail: 'Structure-function claims on the artwork are acceptable for a health supplement.',
  reference: HSA
}, {
  name: 'Website address',
  status: 'Pass',
  detail: 'A website address is disclosed on the artwork.',
  reference: HSA
}, {
  name: 'GMO declaration',
  status: 'Pass',
  detail: 'The non-GMO declaration is an unrestricted voluntary claim.',
  reference: HSA
}];
const sgAdvisoryFindings: Finding[] = [{
  name: 'Allergen declaration status',
  status: 'Fail',
  severity: 'Medium',
  infoRequired: true,
  detail: 'Allergen status is to be confirmed. It is the responsibility of the company to include a warning statement if there is any known risk of allergy caused by any ingredient or by the manufacturing process. Check for any cases of allergy to citrus ingredients.',
  reference: HSA
}, {
  name: 'Use under medical supervision statement',
  status: 'Pass',
  detail: 'The required medical supervision wording appears on the artwork.',
  reference: HSA
}, {
  name: 'Additional warning statements',
  status: 'Pass',
  detail: 'No further warning statements are triggered by this formulation.',
  reference: HSA
}];
const sgRegistrationFindings: Finding[] = [{
  name: 'Food registration number',
  status: 'Not applicable',
  detail: 'Health supplements are not subject to pre-marketing licensing in Singapore, therefore there is no registration number.',
  reference: HSA
}, {
  name: 'Bar code',
  status: 'Pass',
  detail: 'A barcode area is present on the artwork.',
  reference: HSA
}];
const sgFindingSections: FindingSection[] = [{
  title: 'Mandatory Labelling Requirements',
  issues: '5 issues',
  severity: ['3 HIGH', '2 MEDIUM'],
  passRatio: '5/10 PASS',
  findings: sgMandatoryFindings
}, {
  title: 'Actives Declaration',
  issues: '2 issues',
  severity: ['2 MEDIUM'],
  passRatio: '2/4 PASS',
  findings: sgActivesFindings
}, {
  title: 'Claims & Presentation',
  issues: '2 issues',
  severity: ['2 MEDIUM'],
  passRatio: '4/6 PASS',
  findings: sgClaimsFindings
}, {
  title: 'Advisory & Warning Statements',
  issues: '1 issue',
  severity: ['1 MEDIUM'],
  passRatio: '2/3 PASS',
  findings: sgAdvisoryFindings
}, {
  title: 'Registration & Other',
  issues: '0 issues',
  severity: [],
  passRatio: '1/1 PASS',
  notApplicable: '1 NOT APPLICABLE',
  findings: sgRegistrationFindings
}];
const sgCartonRequirements: Requirement[] = [{
  name: 'Brand name',
  status: 'pass'
}, {
  name: 'Product name',
  status: 'pass'
}, {
  name: 'Ingredients list',
  status: 'pass'
}, {
  name: 'Manufacturer and country of origin',
  status: 'pass'
}, {
  name: 'Storage conditions',
  status: 'pass'
}, {
  name: 'Name and address of local importer',
  status: 'fail'
}, {
  name: 'Lot identification',
  status: 'fail',
  note: 'Mandatory on all formats'
}, {
  name: 'Expiry date',
  status: 'fail',
  note: 'Mandatory on all formats'
}];
const sgIndividualRequirements: Requirement[] = sgCartonRequirements.map(requirement => requirement.name === 'Storage conditions' ? {
  name: 'Storage conditions',
  status: 'fail' as RequirementStatus,
  note: 'Follow the same labelling requirement as the outer carton.'
} : requirement);
const sgNonIndividualRequirements: Requirement[] = [{
  name: 'Brand name',
  status: 'voluntary'
}, {
  name: 'Product name',
  status: 'voluntary'
}, {
  name: 'Ingredients list',
  status: 'voluntary'
}, {
  name: 'Manufacturer and country of origin',
  status: 'voluntary'
}, {
  name: 'Storage conditions',
  status: 'voluntary'
}, {
  name: 'Name and address of local importer',
  status: 'voluntary'
}, {
  name: 'Lot identification',
  status: 'fail',
  note: 'Mandatory on all formats'
}, {
  name: 'Expiry date',
  status: 'fail',
  note: 'Mandatory on all formats'
}, {
  name: "'Not labelled for individual sale' statement present",
  status: 'pass'
}, {
  name: "'See carton for ingredients and other product information' statement present",
  status: 'pass'
}];
const topIssues = mandatoryFindings.filter(finding => finding.status === 'Fail').slice(0, 5);
const supplementFacts: {
  nutrient: string;
  perSachet: string;
  per100g: string;
  flag?: 'red' | 'amber';
  flagNote?: string;
}[] = [{
  nutrient: 'Vitamin A (as retinyl acetate)',
  perSachet: '3,600 µg RE',
  per100g: '60,000 µg RE',
  flag: 'red',
  flagNote: 'Exceeds advisory statement threshold'
}, {
  nutrient: 'Vitamin C (as ascorbic acid)',
  perSachet: '550 mg',
  per100g: '9,167 mg'
}, {
  nutrient: 'Zinc (as zinc sulfate)',
  perSachet: '15 mg',
  per100g: '250 mg'
}, {
  nutrient: 'Energy',
  perSachet: '20 kcal',
  per100g: '333 kcal'
}, {
  nutrient: 'Total carbohydrate',
  perSachet: '6 g',
  per100g: '100 g',
  flag: 'amber',
  flagNote: 'Declared value inconsistent with formulation'
}, {
  nutrient: 'Dietary fibre',
  perSachet: '2 g',
  per100g: '33.3 g',
  flag: 'amber',
  flagNote: 'Declared value inconsistent with formulation'
}];
const ingredientComponents: [string, string][] = [['Orange fruit juice powder', '51.133%'], ['Soluble corn fibre', '34.291%'], ['Vitamin C (ascorbic acid)', '11.667%'], ['Silicon dioxide', '1.000%'], ['Zinc (zinc sulfate)', '0.667%'], ['Stevia extract', '0.500%'], ['Vitamin A (retinyl acetate)', '0.400%'], ['Beta carotene', '0.325%']];
const claimCards = [{
  title: 'SUPPORTS IMMUNE FUNCTION; PROMOTES COLLAGEN AND CONNECTIVE TISSUE FORMATION',
  context: 'Indications attributed to vitamin C. Must map to the Permissible Indications Determination.',
  location: 'front'
}, {
  title: 'SUPPORTS IMMUNE FUNCTION, VISION, RED BLOOD CELL AND BONE FORMATION',
  context: 'Four indications attributed to vitamin A in a single statement.',
  location: 'front'
}, {
  title: 'SUPPORTS IMMUNE AND NERVOUS SYSTEM FUNCTION; PROMOTES GROWTH AND DEVELOPMENT',
  context: "Indications attributed to zinc. 'Growth and development' may read as a paediatric claim.",
  location: 'front'
}, {
  title: 'THIS PRODUCT IS NOT INTENDED TO DIAGNOSE, TREAT, CURE, OR PREVENT ANY DISEASE.',
  context: 'United States DSHEA disclaimer. No standing in Australia.',
  location: 'back'
}, {
  title: 'SATISFACTION GUARANTEED',
  context: 'Guarantee-style claim restricted under the Advertising Code.',
  location: 'side'
}];
const extractedFields = [{
  label: 'Net content',
  confidence: 'High confidence',
  value: '15 × 0.21 oz (6 g) packets · NET WT 3.15 oz (90 g)'
}, {
  label: 'Manufacturer address',
  confidence: 'High confidence',
  value: 'Manufactured for Dole Packaged Foods, LLC, Westlake Village, CA 91361'
}, {
  label: 'Country of origin',
  confidence: 'High confidence',
  value: 'Product of Taiwan'
}, {
  label: 'Contact',
  confidence: 'High confidence',
  value: 'Call 1-800-232-8888 (United States)'
}, {
  label: 'Website',
  confidence: 'High confidence',
  value: 'thesecretnatureoffruit.com'
}, {
  label: 'Australian sponsor',
  confidence: 'Low confidence',
  value: 'not detected'
}, {
  label: 'Date marking',
  confidence: 'Low confidence',
  value: 'not detected'
}, {
  label: 'Lot identification',
  confidence: 'Low confidence',
  value: 'not detected'
}, {
  label: 'AUST L number',
  confidence: 'Low confidence',
  value: 'not detected'
}, {
  label: 'Advisory statements',
  confidence: 'Low confidence',
  value: 'not detected'
}];
const documents = [['Orange_15ct_Carton_R4.pdf', 'all faces', 'PDF', '2.7 MB', '26 Jul 2026']];
interface PackagingLabelTabProps {
  onBackClick?: () => void;
  market?: MarketVariant;
  onViewFormula?: () => void;
}
export function PackagingLabelTab({
  market = 'AU',
  onViewFormula
}: PackagingLabelTabProps) {
  const [packFormat, setPackFormat] = useState<PackFormat>('carton');
  const [reportTab, setReportTab] = useState<ReportTab>('overview');
  const [showArtwork, setShowArtwork] = useState(false);
  const isSingapore = market === 'SG';
  const isNonIndividual = packFormat === 'sachet-not-individual';
  const isIndividual = packFormat === 'sachet-individual';
  const sections = isSingapore ? sgFindingSections : findingSections;
  const findingsBadge = isSingapore ? '10' : '25';
  const metrics = isSingapore ? {
    critical: '3',
    warnings: '7',
    inconclusive: '0/25'
  } : {
    critical: '12',
    warnings: '13',
    inconclusive: '2/46'
  };
  const requirements = isSingapore ? isNonIndividual ? sgNonIndividualRequirements : isIndividual ? sgIndividualRequirements : sgCartonRequirements : isNonIndividual ? nonIndividualRequirements : cartonRequirements;
  const score = isSingapore ? isNonIndividual ? 82 : isIndividual ? 64 : 68 : isNonIndividual ? 72 : 58;
  const passText = isSingapore ? isNonIndividual ? '2/10 PASS' : isIndividual ? '4/8 PASS' : '5/8 PASS' : isNonIndividual ? '2/10 PASS' : '6/8 PASS';
  const highText = isSingapore ? '3 HIGH' : '2 HIGH';
  return <div className="space-y-5" data-id="element-1231" data-mp="1g2t13pl7cmd2">
      <ReportHeader score={score} market={market} metrics={metrics} onOpenArtwork={() => {
      setReportTab('findings');
      setShowArtwork(true);
    }} data-id="element-1232" data-mp="1g31dzhhbrro9" />
      <nav className="flex overflow-x-auto border-b border-[#dce3da] bg-white px-2" aria-label="Label report sections" data-id="element-1233" data-mp="nexb13q51sp9">
        {reportTabs.map(tab => <button key={tab.id} type="button" onClick={() => setReportTab(tab.id)} aria-current={reportTab === tab.id ? 'page' : undefined} className={`relative shrink-0 px-4 py-3.5 text-sm font-semibold transition ${reportTab === tab.id ? 'text-evergreen' : 'text-[#718174] hover:text-evergreen'}`} data-id="element-1234" data-mp="ihnaarxatum4">{tab.label}{tab.id === 'findings' && <span className="ml-1.5 rounded-full bg-red-50 px-1.5 py-0.5 font-mono text-[10px] text-red-700" data-id="element-1235" data-mp="27wv3u7nptwx4">{findingsBadge}</span>}{reportTab === tab.id && <span className="absolute inset-x-4 bottom-0 h-0.5 rounded-full bg-forest-green" data-id="element-1236" data-mp="2bcogz5o2dxa0" />}</button>)}
      </nav>
      {reportTab === 'overview' && <OverviewReport sections={sections} requirements={requirements} highText={highText} passText={passText} packFormat={packFormat} onPackFormatChange={setPackFormat} onViewFindings={() => setReportTab('findings')} data-id="element-1237" data-mp="2g9y4ugvt20vq" />}
      {reportTab === 'findings' && <FindingsReport sections={sections} market={market} showArtwork={showArtwork} onToggleArtwork={() => setShowArtwork(open => !open)} onViewFormula={() => onViewFormula?.()} data-id="element-1238" data-mp="2u35wu9nssydy" />}
      {reportTab === 'product-data' && <ProductDataReport data-id="element-1239" data-mp="2kckymnc1dvvk" />}
      {reportTab === 'documents' && <DocumentsReport data-id="element-1240" data-mp="2pcpj0og2far2" />}
      <p className="py-4 text-center font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#879389]" data-id="element-1241" data-mp="2pbxlv96tl4yo">ID · 8E2F6A41-B7C3-4D19-A552-91C4E7B30D62 · JUL 24, 2026</p>
    </div>;
}
function UploadState({
  scope,
  onScopeChange,
  onAnalyze,
  onBack
}: {
  scope: string;
  onScopeChange: (value: string) => void;
  onAnalyze: () => void;
  onBack: () => void;
}) {
  return <div className="mx-auto max-w-3xl" data-id="element-1242" data-mp="smq2zjqdyuka"><label className="block" data-id="element-1243" data-mp="sly8ydcft4w4"><span className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#718174]" data-id="element-1244" data-mp="sl56hokih1js">Product scope (optional)</span><input value={scope} onChange={event => onScopeChange(event.target.value)} className="mt-2 w-full rounded-lg border border-[#d6dfd3] bg-white px-3 py-2.5 text-sm outline-none focus:border-forest-green focus:ring-2 focus:ring-forest-green/15" data-id="element-1245" data-mp="xfj5e0ydtqt7" /><span className="mt-2 block text-sm text-[#6c7d70]" data-id="element-1246" data-mp="9n2dkyouekgp">Refines the label-obligation checks. Leave empty if no scope is required.</span></label><section className="mt-6 rounded-2xl border border-[#dce3da] bg-white px-6 py-8 text-center shadow-sm" data-id="element-1247" data-mp="elncmuscyy3k"><h2 className="font-[Figtree] text-2xl font-semibold text-evergreen" data-id="element-1248" data-mp="jncrymmm5xxg">Upload label artwork</h2><p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-[#6b7d70]" data-id="element-1249" data-mp="1q1u6dcpwhtqj">Send 1 to 8 faces (front, back, sides). Multi-face is one call and counts as a single analysis.</p><div className="mt-7 rounded-2xl border-2 border-dashed border-[#cad7c6] bg-[#fbfdf9] px-6 py-10" data-id="element-1250" data-mp="3fi7m3n1nomyq"><span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#e9f1e6] text-forest-green" data-id="element-1251" data-mp="3al7e36l8xa4z"><UploadCloud className="h-7 w-7" data-id="element-1252" data-mp="35s4sbkmpn4zc" /></span><h3 className="mt-4 font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-1253" data-mp="35r39gtzgde72">Upload label artwork</h3><p className="mt-2 text-sm text-[#718174]" data-id="element-1254" data-mp="30sif3b6vtoqj">Accepted: JPEG, PNG, WebP, PDF · Max size: 10 MB per file</p><button type="button" onClick={onAnalyze} className="mt-6 inline-flex items-center gap-2 rounded-xl border border-[#d3ded0] bg-white px-4 py-2.5 text-sm font-semibold text-evergreen hover:bg-[#f7faf5]" data-id="element-1255" data-mp="2rtocn46ve452"><FileText className="h-4 w-4" data-id="element-1256" data-mp="2hvgysu7vo27a" />Pick from library</button><p className="mt-4 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#78877b]" data-id="element-1257" data-mp="2hx1syiqa5j4m">Up to 8 files</p></div></section><div className="mt-5 flex flex-col items-center gap-4" data-id="element-1258" data-mp="l0umtt2tcke2"><button type="button" onClick={onAnalyze} className="rounded-xl bg-forest-green px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-evergreen" data-id="element-1259" data-mp="g55k5gvhyndj">Analyze packaging</button><button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-sm font-semibold text-forest-green hover:underline" data-id="element-1260" data-mp="2fbkoql7fj31p"><ArrowLeft className="h-4 w-4" data-id="element-1261" data-mp="20npuh0drwqmi" />Back to dashboard</button></div></div>;
}
function ReportHeader({
  score,
  market = 'AU',
  metrics = {
    critical: '12',
    warnings: '13',
    inconclusive: '2/46'
  },
  onOpenArtwork
}: {
  score: number;
  market?: MarketVariant;
  metrics?: {
    critical: string;
    warnings: string;
    inconclusive: string;
  };
  onOpenArtwork: () => void;
}) {
  return <><div data-id="element-1262" data-mp="2jd5wjs53wr51"><p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b968d]" data-id="element-1263" data-mp="2jdfm5em8owa7">Label score by market</p><div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-1264" data-mp="2oapgn8ohjy4w"><span className="inline-flex items-center gap-2 rounded-full border border-forest-green/30 bg-white px-3 py-1.5 font-mono text-sm font-semibold text-evergreen" data-id="element-1265" data-mp="17xh96znxxq3p"><span className="text-base" data-id="element-1266" data-mp="1cy56u46od56w">{market === 'SG' ? '🇸🇬' : '🇦🇺'}</span>{market} <span className="text-forest-green" data-id="element-1267" data-mp="3r3a81477wmjb">{score}</span></span><button type="button" className="rounded-lg border border-[#d5ded2] bg-white px-3 py-1.5 text-sm font-semibold text-evergreen hover:bg-[#f7faf5]" data-id="element-1268" data-mp="3m58oki5vql7b">+ Add</button></div></div><div className="flex items-start gap-3 rounded-xl border border-[#dbe4d8] bg-[#f5f8f3] px-4 py-3 text-sm leading-relaxed text-[#5f7063]" data-id="element-1269" data-mp="3yubelffqf9z"><FileText className="mt-0.5 h-4 w-4 shrink-0" data-id="element-1270" data-mp="38dlr4f07b6my" />PDF artwork detected · This report was generated from a print-ready PDF artwork. Verifications relying on on-pack position or legibility may need manual review.</div><section className="overflow-hidden rounded-2xl border border-red-200 bg-white shadow-sm" data-id="element-1271" data-mp="3h6p28gndj7ef"><div className="flex flex-col gap-4 bg-red-50 px-5 py-5 lg:flex-row lg:items-center lg:justify-between" data-id="element-1272" data-mp="3cfp2z4qs8d8r"><div className="flex items-start gap-3" data-id="element-1273" data-mp="2opmc2ryltdbq"><span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700" data-id="element-1274" data-mp="2oojmcnd0pgxe"><CircleX className="h-6 w-6" data-id="element-1275" data-mp="2knre9xfvti2p" /></span><div data-id="element-1276" data-mp="2xdibcttlc85t"><h2 className="font-[Figtree] text-xl font-semibold leading-tight text-red-900" data-id="element-1277" data-mp="2xdi72hmk5m00">Non compliant</h2><p className="mt-1 text-sm text-red-800" data-id="element-1278" data-mp="32cl6nmjrr00l">One or more critical checks failed. Changes are required before this label can ship.</p></div></div><div className="flex flex-wrap gap-2" data-id="element-1279" data-mp="37amx4sjiy1m3"><Action icon={<UploadCloud className="h-4 w-4" data-id="element-1281" data-mp="379v51rp4ym0k" />} data-id="element-1280" data-mp="379lcpxgmc2rp">New analysis</Action><Action icon={<Download className="h-4 w-4" data-id="element-1283" data-mp="3c3pjaflz28s9" />} data-id="element-1282" data-mp="3c36bvsty72sh">Download PDF</Action><Action icon={<Pencil className="h-4 w-4" data-id="element-1285" data-mp="3h0fxsyi4owbk" />} data-id="element-1284" data-mp="3h20tirg6ilb0">Review report</Action><button type="button" aria-label="Report settings" className="rounded-lg border border-[#d8e0d6] bg-white p-2 text-evergreen hover:bg-[#f7faf5]" data-id="element-1286" data-mp="3h3tzs5mvr3ih"><Settings className="h-4 w-4" data-id="element-1287" data-mp="3q1w5uzlh0mcm" /></button></div></div><div className="flex flex-col gap-5 px-5 py-6 lg:flex-row lg:items-center" data-id="element-1288" data-mp="3q0ct3iz0wtfl"><ReadinessRing score={score} size={110} label="Label compliance score" data-id="element-1289" data-mp="3v0qrsaeqir0y" /><button type="button" onClick={onOpenArtwork} title="View label artwork" className="h-28 w-20 shrink-0 overflow-hidden rounded-md border border-[#d8dfd5] bg-[#fff9ed] shadow-sm transition hover:border-forest-green" data-id="element-1290" data-mp="1vp6f2qqeft9g"><img src={orangeArtwork} alt="Orange Immunity Powder carton artwork" className="h-full w-full object-cover" data-id="element-1291" data-mp="1t4i5ivt8gc8b" /></button><div className="min-w-0" data-id="element-1292" data-mp="1obp3ifp5fhzx"><p className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#718174]" data-id="element-1293" data-mp="1ob5lkyzi0tlw">Product</p><h2 className="mt-1 font-[Figtree] text-2xl font-semibold text-evergreen" data-id="element-1294" data-mp="1eeu4lvoyrfl4">Orange Immunity Powder</h2><p className="mt-1 text-sm text-[#718174]" data-id="element-1295" data-mp="2c2kp2v30hgyq">3 label faces analysed · 26 Jul 2026</p><div className="mt-3 flex flex-wrap gap-2" data-id="element-1296" data-mp="2c0i08edlhtvl"><Pill data-id="element-1297" data-mp="272yeyuihcgu5">Brand withheld</Pill><Pill icon={<Package className="h-3.5 w-3.5" data-id="element-1299" data-mp="274116p4uxv3q" />} data-id="element-1298" data-mp="274sx612d9uuj">Health supplement</Pill><Pill icon={<Globe2 className="h-3.5 w-3.5" data-id="element-1301" data-mp="223lntxkksnlr" />} data-id="element-1300" data-mp="223vfdpl9r2tk">{market === 'SG' ? 'Singapore' : 'Australia'}</Pill><Pill data-id="element-1302" data-mp="1x5toiihlpz7j">26 Jul 2026</Pill></div></div></div><div className="grid border-t border-[#eadede] sm:grid-cols-3" data-id="element-1303" data-mp="1x4sf0ig8hkrx"><Metric icon={<AlertTriangle className="h-5 w-5 text-red-700" data-id="element-1305" data-mp="qqatz9ifyqpv" />} value={metrics.critical} label="Critical issues" data-id="element-1304" data-mp="qphxd1d75hlg" /><Metric icon={<AlertTriangle className="h-5 w-5 text-amber-700" data-id="element-1307" data-mp="lolk3x6ygj3d" />} value={metrics.warnings} label="Warnings" data-id="element-1306" data-mp="lp3rnpz2ijql" /><Metric icon={<Info className="h-5 w-5 text-[#718174]" data-id="element-1309" data-mp="39wvdvuxow2hi" />} value={metrics.inconclusive} label="Inconclusive" data-id="element-1308" data-mp="39wloo9kgxzcv" /></div></section><div className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-1310" data-mp="3dvkz29oyyobp"><div className="flex gap-3" data-id="element-1311" data-mp="34whet2e2g2vm"><AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" data-id="element-1312" data-mp="34vpjlsods75v" /><div data-id="element-1313" data-mp="2q7kzck9ojnkz"><h2 className="font-semibold text-amber-950" data-id="element-1314" data-mp="2q7us2fm408ud">Regulatory change impact</h2><p className="mt-1 text-sm text-amber-900" data-id="element-1315" data-mp="2uytbvp6b3p2d">3 regulatory changes in DEMO_MOCK_LABEL_TGO92</p><div className="mt-2 flex gap-2" data-id="element-1316" data-mp="2v0nz5a51vt9m"><span className="rounded-full border border-amber-300 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase text-amber-900" data-id="element-1317" data-mp="2h7y7ap2ubu4l">Labelling</span><span className="rounded-full border border-amber-300 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase text-amber-900" data-id="element-1318" data-mp="2m6rdrxjpmkv9">New requirement</span></div></div></div><div className="flex gap-2" data-id="element-1319" data-mp="2c7a83g4gk6za"><Action data-id="element-1320" data-mp="2c7to2rajslao">View alert ›</Action><Action data-id="element-1321" data-mp="2ca69xfjffft0">⟳ Re-assess</Action></div></div></>;
}
function Action({
  children,
  icon
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-[#d8e0d6] bg-white px-3 py-2 text-sm font-semibold text-evergreen hover:bg-[#f7faf5]" data-id="element-1322" data-mp="1olzv3xjld4yz">{icon}{children}</button>;
}
function Pill({
  children,
  icon
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return <span className="inline-flex items-center gap-1.5 rounded-full border border-[#dbe3d8] bg-[#f3f6f1] px-2.5 py-1 text-xs font-medium text-evergreen" data-id="element-1323" data-mp="2bkcm7dwt986d">{icon}{children}</span>;
}
function Metric({
  icon,
  value,
  label
}: {
  icon: React.ReactNode;
  value: string;
  label: string;
}) {
  return <div className="flex items-center gap-3 border-b border-[#eadede] px-5 py-4 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0" data-id="element-1324" data-mp="2gbm9w8ysu1fm"><span data-id="element-1325" data-mp="2v9wp67q7vm6h">{icon}</span><div data-id="element-1326" data-mp="2v7akcfupnkry"><p className="font-[Figtree] text-xl font-semibold text-evergreen" data-id="element-1327" data-mp="2v7agu5vi52n7">{value}</p><p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[#718174]" data-id="element-1328" data-mp="2qbv97vo382z8">{label}</p></div></div>;
}
function OverviewReport({
  sections: findingSections,
  requirements,
  highText,
  passText,
  packFormat,
  onPackFormatChange,
  onViewFindings
}: {
  sections: FindingSection[];
  requirements: Requirement[];
  highText: string;
  passText: string;
  packFormat: PackFormat;
  onPackFormatChange: (format: PackFormat) => void;
  onViewFindings: () => void;
}) {
  return <div className="space-y-5" data-id="element-1329" data-mp="s89if559770p"><div className="grid gap-5 xl:grid-cols-3" data-id="element-1330" data-mp="s8t3guq7abiy"><section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm xl:col-span-2" data-id="element-1331" data-mp="x6ks9fvjthix"><div className="flex items-center justify-between border-b border-[#e5ebe3] px-5 py-4" data-id="element-1332" data-mp="125nuyto23bnq"><h2 className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-1333" data-mp="123t9ne8vhhj4">Top issues</h2><button type="button" onClick={onViewFindings} className="font-mono text-xs font-semibold tracking-[0.1em] text-forest-green hover:underline" data-id="element-1334" data-mp="16xp96l4sbcge">VIEW ALL →</button></div><div className="divide-y divide-[#edf0eb]" data-id="element-1335" data-mp="ef1qtdh6abj0">{(findingSections[0]?.findings.filter(finding => finding.status === 'Fail').slice(0, 5) ?? topIssues).map(issue => <div key={issue.name} className="flex gap-3 px-5 py-4" data-id="element-1336" data-mp="2normc8tv33a3"><span className={`mt-0.5 h-fit rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] ${issue.severity === 'Medium' ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700'}`} data-id="element-1337" data-mp="2np9ht38p9dgy">{issue.severity ?? 'High'}</span><div data-id="element-1338" data-mp="28rh3ufiswx4v"><h3 className="text-sm font-semibold text-evergreen" data-id="element-1339" data-mp="28r8pskaxzls3">{issue.name}</h3><p className="mt-1 text-sm text-[#718174]" data-id="element-1340" data-mp="28tlgpmyoh6ha">{issue.detail}</p></div></div>)}</div></section><section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" data-id="element-1341" data-mp="23zh86z9rkqe3"><div className="border-b border-[#e5ebe3] px-5 py-4" data-id="element-1342" data-mp="1zzovcadc7y2x"><h2 className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-1343" data-mp="1v0cblyhrj74k">Sections</h2></div><div className="divide-y divide-[#edf0eb]" data-id="element-1344" data-mp="1q0znzbj2hw0x">{findingSections.map(section => <button key={section.title} type="button" onClick={onViewFindings} className="flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left text-sm font-medium text-evergreen hover:bg-[#f7faf5]" data-id="element-1345" data-mp="1q33qpq58cozm"><span data-id="element-1346" data-mp="rqnl0xjfg688">{section.title}</span><span className="rounded-full bg-red-50 px-2 py-0.5 font-mono text-xs font-semibold text-red-700" data-id="element-1347" data-mp="my48m3wh7h50">{section.issues.split(' ')[0]}</span></button>)}</div></section></div><PackFormatControl packFormat={packFormat} onChange={onPackFormatChange} data-id="element-1348" data-mp="hz15wq3yxn07" /><section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" data-id="element-1349" data-mp="d0zijszif1ix"><div className="flex flex-col gap-3 border-b border-[#e5ebe3] px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-1350" data-mp="1amdqiuqkjkqv"><div data-id="element-1351" data-mp="15ovdas5iqjd2"><h2 className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-1352" data-mp="15p558la4j0le">Mandatory Labelling Requirements</h2><p className="mt-1 text-sm text-[#708174]" data-id="element-1353" data-mp="10nful6qtcoxu">Checked against the selected Australian pack format.</p></div><div className="flex items-center gap-2" data-id="element-1354" data-mp="wpqgprdip03j"><span className="rounded-full bg-red-50 px-2.5 py-1 font-mono text-[11px] font-semibold text-red-700" data-id="element-1355" data-mp="3lfzr53j5bhg9">{highText}</span><span className="rounded-full bg-forest-green/10 px-2.5 py-1 font-mono text-[11px] font-semibold text-forest-green" data-id="element-1356" data-mp="3hctnm3nf44dh">{passText}</span></div></div><div className="divide-y divide-[#edf0eb]" data-id="element-1357" data-mp="1wtraf4nz4onm">{requirements.map(requirement => <RequirementRow key={requirement.name} requirement={requirement} data-id="element-1358" data-mp="20xyxyyip3yk9" />)}</div></section></div>;
}
function PackFormatControl({
  packFormat,
  onChange
}: {
  packFormat: PackFormat;
  onChange: (format: PackFormat) => void;
}) {
  return <section className="rounded-2xl border border-[#dce3da] bg-white p-5 shadow-sm" data-id="element-1359" data-mp="2apbonmsrciop"><h2 className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-1360" data-mp="2fltw4ufh2v4q">Pack format</h2><p className="mt-1 text-sm text-[#6c7d70]" data-id="element-1361" data-mp="2kk3o6w5whfm4">Requirements differ by pack format. Switching re-runs the obligation checks.</p><div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Pack format" data-id="element-1362" data-mp="2ont99bnd9f2e">{formats.map(format => <button key={format.id} type="button" onClick={() => onChange(format.id)} aria-pressed={packFormat === format.id} className={`rounded-lg border px-3 py-2 text-sm font-semibold ${packFormat === format.id ? 'border-forest-green bg-forest-green text-white' : 'border-[#d6dfd3] bg-white text-[#617366] hover:bg-[#f7faf5]'}`} data-id="element-1363" data-mp="2tk0j3fivvapm">{format.label}</button>)}</div></section>;
}
const orangeArtwork = 'https://cdn.magicpatterns.com/uploads/a3PoVyhVbRDEeL7bptPjtw/Screenshot_2026-07-29_at_9.27.40_PM.png';
const artworkFaces: {
  id: string;
  label: string;
  src: string;
}[] = [{
  id: 'front',
  label: 'Front',
  src: orangeArtwork
}, {
  id: 'back',
  label: 'Back',
  src: orangeArtwork
}, {
  id: 'side',
  label: 'Side',
  src: orangeArtwork
}];
const findingFaces: Record<string, string> = {
  'Actives declaration format': 'back',
  'United States Supplement Facts panel format used': 'back',
  'Nutrition Information Panel present': 'back',
  'Percent Daily Value footnote': 'back',
  "'Percent Daily Values are based on a 2,000 calorie diet' footnote": 'back',
  'Total carbohydrate declaration inconsistent with formulation': 'back',
  'Dietary fibre declaration inconsistent with formulation': 'back',
  'Legal name / product designation': 'front',
  'Legal name': 'front',
  'Net content declaration': 'front',
  'Net content': 'front',
  'Fruit emphasis in health claim': 'front',
  'Indications not drawn from the Permissible Indications Determination': 'front',
  'Australian sponsor name and address': 'side',
  'Name and address of local importer': 'side',
  'Storage conditions': 'side',
  'Batch or lot identification': 'side',
  'Lot identification': 'side',
  'Expiry date': 'side',
  'Australian contact details': 'side'
};
function FindingsReport({
  sections,
  market,
  showArtwork,
  onToggleArtwork,
  onViewFormula
}: {
  sections: FindingSection[];
  market: MarketVariant;
  showArtwork: boolean;
  onToggleArtwork: () => void;
  onViewFormula: () => void;
}) {
  const [activeFace, setActiveFace] = useState('front');
  const [enlarged, setEnlarged] = useState(false);
  const faceIndex = artworkFaces.findIndex(face => face.id === activeFace);
  const face = artworkFaces[faceIndex] ?? artworkFaces[0];
  return <section aria-label="Findings" data-id="element-1364" data-mp="2l9e3roijoj9i">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-1365" data-mp="2e4msdekpys5e">
        <h2 className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-1366" data-mp="295a8n2p5a171">Findings</h2>
        <button type="button" onClick={onToggleArtwork} aria-expanded={showArtwork} className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5] sm:self-auto" data-id="element-1367" data-mp="24ea9dqsjz71d">
          <ImageIcon className="h-4 w-4" data-id="element-1368" data-mp="1qdhdb1mls3w5" />
          {showArtwork ? 'Hide artwork' : 'View label artwork'}
        </button>
      </div>

      {showArtwork ? <div className="mt-4 flex flex-col gap-4 lg:flex-row" data-id="element-1369" data-mp="1ggcosv5abwc6">
          <div className="max-h-[640px] overflow-y-auto lg:w-2/5 lg:shrink-0" data-id="element-1370" data-mp="1ghw68p2ogdfj">
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Label face" data-id="element-1371" data-mp="3fsdzglw0sb15">
              {artworkFaces.map(option => <button key={option.id} type="button" onClick={() => setActiveFace(option.id)} aria-pressed={option.id === activeFace} className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${option.id === activeFace ? 'border-forest-green bg-forest-green/10 text-evergreen' : 'border-[#dce3da] bg-white text-[#617366] hover:bg-[#f7faf5]'}`} data-id="element-1372" data-mp="3as9n9825a2gq">
                  {option.label}
                </button>)}
            </div>
            <p className="mt-2 text-xs text-[#8b968d]" data-id="element-1373" data-mp="34fx8sxg5lney">Face {faceIndex + 1} of {artworkFaces.length} · Orange_15ct_Carton_R4.pdf</p>
            <button type="button" onClick={() => setEnlarged(true)} className="group relative mt-2 block w-full overflow-hidden rounded-xl border border-[#e1e8df] bg-[#f6f8f5]" data-id="element-1374" data-mp="3ect7yck5wx6u">
              <img src={face.src} alt={`${face.label} face of the label artwork`} className="aspect-[4/3] w-full object-contain" data-id="element-1375" data-mp="tm2ifw6cuqwy" />
              <span className="absolute bottom-2 right-2 rounded-full bg-evergreen/85 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-white opacity-0 transition group-hover:opacity-100" data-id="element-1376" data-mp="3x436hnodlaj">Click to enlarge</span>
            </button>
          </div>
          <div className="max-h-[640px] flex-1 overflow-y-auto pr-1" data-id="element-1377" data-mp="8x9olg3cqbk6">
            <FindingsList sections={sections} market={market} onViewFormula={onViewFormula} onFindingOpen={name => {
          const target = findingFaces[name];
          if (target) setActiveFace(target);
        }} data-id="element-1378" data-mp="8ux7t69by78p" />
          </div>
        </div> : <div className="mt-4" data-id="element-1379" data-mp="3r2d0pkrqjnu3">
          <FindingsList sections={sections} market={market} onViewFormula={onViewFormula} data-id="element-1380" data-mp="3r3emaj6dnsq2" />
        </div>}

      {enlarged && <div role="dialog" aria-modal="true" aria-label="Label artwork" className="fixed inset-0 z-50 flex items-center justify-center bg-evergreen/70 p-6" onClick={() => setEnlarged(false)} data-id="element-1381" data-mp="3m9tniowymupt">
          <div className="w-full max-w-4xl" onClick={event => event.stopPropagation()} data-id="element-1382" data-mp="2tl4t18qetnn1">
            <div className="flex items-center justify-between pb-3" data-id="element-1383" data-mp="193vmhp1kws56">
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.14em] text-white" data-id="element-1384" data-mp="1dwyf9uyj7xkb">{face.label} · Orange_15ct_Carton_R4.pdf</p>
              <button type="button" onClick={() => setEnlarged(false)} className="rounded-lg bg-white/15 p-2 text-white hover:bg-white/25" aria-label="Close artwork preview" data-id="element-1385" data-mp="z5613mah4pk6">
                <X className="h-4 w-4" data-id="element-1386" data-mp="1rw7ogaoblres" />
              </button>
            </div>
            <img src={face.src} alt={`${face.label} face of the label artwork, enlarged`} className="w-full rounded-xl bg-white object-contain" data-id="element-1387" data-mp="1wtpthpz7vmhi" />
          </div>
        </div>}
    </section>;
}
function FindingsList({
  sections: findingSections,
  market,
  onFindingOpen,
  onViewFormula
}: {
  sections: FindingSection[];
  market: MarketVariant;
  onFindingOpen?: (name: string) => void;
  onViewFormula?: () => void;
}) {
  const [openSection, setOpenSection] = useState<string | null>(market === 'SG' ? 'Mandatory Labelling Requirements' : 'Ingredient Compliance');
  const [openFinding, setOpenFinding] = useState<string | null>(null);
  const [showInconclusive, setShowInconclusive] = useState(false);
  return <div className="space-y-4" data-id="element-1388" data-mp="u5ci33fydteu">{findingSections.map(section => <section key={section.title} className="overflow-hidden rounded-2xl border border-[#dce3da] border-l-4 border-l-red-500 bg-white shadow-sm" data-id="element-1389" data-mp="p583xk2ipars"><button type="button" onClick={() => setOpenSection(open => open === section.title ? null : section.title)} aria-expanded={openSection === section.title} className="flex w-full flex-col gap-3 px-5 py-4 text-left sm:flex-row sm:items-center" data-id="element-1390" data-mp="1lufr0wisbrxt"><div className="flex-1" data-id="element-1391" data-mp="1d0u9j5f8jwkp"><h2 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-1392" data-mp="184c5y8vn649y">{section.title}</h2>{section.subtitle && <p className="mt-1 text-sm text-[#718174]" data-id="element-1393" data-mp="25jzx3z7hmm25">{section.subtitle}</p>}<p className="mt-1 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#748477]" data-id="element-1394" data-mp="25layxthdhjqm">{section.issues}</p></div>{section.linkToFormula && onViewFormula && <span role="link" tabIndex={0} onClick={event => {
          event.stopPropagation();
          onViewFormula();
        }} onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            onViewFormula();
          }
        }} className="cursor-pointer text-sm font-semibold text-forest-green hover:underline" data-id="element-1395" data-mp="285h1bwqc9252">View ingredient verdicts →</span>}<div className="flex items-center gap-2" data-id="element-1396" data-mp="2r31uhg04ymvh">{section.severity.map(chip => <span key={chip} className="rounded-full bg-red-50 px-2 py-0.5 font-mono text-[10px] font-semibold text-red-700" data-id="element-1397" data-mp="2r0xpeuqi3htm">{chip}</span>)}<span className="rounded-full bg-forest-green/10 px-2 py-0.5 font-mono text-[10px] font-semibold text-forest-green" data-id="element-1398" data-mp="30stl5z546vuo">{section.passRatio}</span>{section.notApplicable && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-semibold text-slate-600" data-id="element-1399" data-mp="35tr2k9iavlv4">{section.notApplicable}</span>}<ChevronDown className={`h-4 w-4 text-[#718174] transition ${openSection === section.title ? 'rotate-180' : ''}`} data-id="element-1400" data-mp="3ertehk3knyx6" /></div></button>{openSection === section.title && <div className="border-t border-[#edf0eb]" data-id="element-1401" data-mp="legv4dx5unmv">{section.findings.map(finding => <FindingDisclosure key={finding.name} finding={finding} open={openFinding === finding.name} onToggle={() => {
          setOpenFinding(open => open === finding.name ? null : finding.name);
          onFindingOpen?.(finding.name);
        }} data-id="element-1402" data-mp="pf9210ik8gfz" />)}</div>}</section>)}{market === 'AU' && <section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" data-id="element-1403" data-mp="1422c9rd82w1b"><button type="button" onClick={() => setShowInconclusive(open => !open)} aria-expanded={showInconclusive} className="flex w-full items-center justify-between px-5 py-4" data-id="element-1404" data-mp="191f831m2ndg1"><div data-id="element-1405" data-mp="uaodjlt3i098"><h2 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-1406" data-mp="uay71jdcmpjo">Inconclusive</h2><span className="mt-1 inline-flex rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-semibold text-slate-600" data-id="element-1407" data-mp="z9soohzky827">2 INCONCLUSIVE</span></div><ChevronDown className={`h-4 w-4 text-[#718174] transition ${showInconclusive ? 'rotate-180' : ''}`} data-id="element-1408" data-mp="1rsg5hl7jn0xh" /></button>{showInconclusive && <div className="divide-y divide-[#edf0eb] border-t border-[#edf0eb]" data-id="element-1409" data-mp="3ch0qwfx41yxp">{['On-pack legibility of the side panel — could not be verified from a print-ready PDF', 'Tactile/braille identification presence — not assessable from artwork'].map(item => <p key={item} className="px-5 py-4 text-sm text-[#617366]" data-id="element-1410" data-mp="3h9aehpar6mwt">{item}</p>)}</div>}</section>}</div>;
}
function FindingDisclosure({
  finding,
  open,
  onToggle
}: {
  finding: Finding;
  open: boolean;
  onToggle: () => void;
}) {
  return <article className="border-b border-[#edf0eb] last:border-b-0" data-id="element-1411" data-mp="2olegmh9g6oyg"><button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center gap-3 px-5 py-3.5 text-left hover:bg-[#fafcf9]" data-id="element-1412" data-mp="2omzdidj8ijzh"><span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${finding.status === 'Fail' ? 'bg-red-100 text-red-700' : finding.status === 'Not applicable' ? 'bg-slate-100 text-slate-500' : 'bg-forest-green/10 text-forest-green'}`} data-id="element-1413" data-mp="2xk9n7ugem12a">● {finding.status}</span>{finding.severity && <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold uppercase ${finding.severity === 'High' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'}`} data-id="element-1414" data-mp="36nv1byf5k1hr">{finding.severity}</span>}{finding.infoRequired && <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 font-mono text-[10px] font-semibold text-amber-800" data-id="element-1415" data-mp="3fo9oz4fl9sof"><Info className="h-3 w-3" data-id="element-1416" data-mp="2nwka1g5ilmpc" />Info required</span>}<span className={`flex-1 text-sm font-semibold ${finding.status === 'Not applicable' ? 'text-[#8b968d]' : 'text-evergreen'}`} data-id="element-1417" data-mp="2nxvdtfyymugg">{finding.name}</span><ChevronDown className={`h-4 w-4 text-[#718174] transition ${open ? 'rotate-180' : ''}`} data-id="element-1418" data-mp="2wx8tiqdogf9m" /></button>{open && <div className="space-y-4 bg-[#fafcf9] px-5 pb-5" data-id="element-1419" data-mp="31np6689obwux"><div className="flex gap-2" data-id="element-1420" data-mp="31pjrvosrrt02"><button type="button" className="inline-flex items-center gap-1 rounded-full border border-[#d6dfd3] px-2.5 py-1 text-xs font-medium text-evergreen" data-id="element-1421" data-mp="31ptjfgtgq87v">AI agent <ThumbsUp className="h-3.5 w-3.5" data-id="element-1422" data-mp="296mm91bhyz0o" /><ThumbsDown className="h-3.5 w-3.5" data-id="element-1423" data-mp="295l5qhbppkbk" /></button></div><div data-id="element-1424" data-mp="21oeuxwifcn2u"><p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#718174]" data-id="element-1425" data-mp="21n3ti3cgbrew">Regulatory reference</p><p className="mt-1" data-id="element-1426" data-mp="1wplje9mp765b"><RegulationLink text={finding.reference ?? 'TGO 92 — Standard for labels of non-prescription medicines'} data-id="element-1427" data-mp="1wmzcmc7msuo5" /></p><blockquote className="mt-3 border-l-2 border-lime-glow pl-3 text-sm italic leading-relaxed text-[#617366]" data-id="element-1428" data-mp="1rrtrhfm1zk14">“{finding.detail}”</blockquote>{finding.recommendation && <p className="mt-3 text-sm font-medium text-evergreen" data-id="element-1429" data-mp="1iq2km7fs175o">Recommendation: {finding.recommendation}</p>}{finding.comparison && <IngredientComparison data-id="element-1430" data-mp="1dwhmmfe6od6h" />}</div><details data-id="element-1431" data-mp="18z7zx7i6ifmc"><summary className="cursor-pointer font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#617366]" data-id="element-1432" data-mp="18yy8rglee2f2">› Show verification logic</summary><p className="mt-2 text-sm text-[#617366]" data-id="element-1433" data-mp="1427tuwlbxcv8">The analysis searches supplied artwork for the required element, evaluates it against the selected market rule, and records the evidence source.</p></details><div data-id="element-1434" data-mp="v0i2trvd0fxz"><p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#718174]" data-id="element-1435" data-mp="v0i0vmbsu5vc">Citations</p><div className="mt-2 rounded-lg border border-[#e0e7dd] bg-white p-3" data-id="element-1436" data-mp="2v42g7e7tvo7i"><div className="flex flex-wrap items-center gap-2" data-id="element-1437" data-mp="2z29nzjkbdf6k"><span className="font-mono text-xs text-evergreen" data-id="element-1438" data-mp="2lc6592kn83lv">{finding.reference ?? 'TGO 92'} · p.1</span><span className="rounded bg-[#f0f3ef] px-1.5 py-0.5 font-mono text-[10px] text-[#617366]" data-id="element-1439" data-mp="2qb9d0us0cnxj">PREVIEW</span></div><p className="mt-2 text-sm italic text-[#617366]" data-id="element-1440" data-mp="2be8jclq9aiwf">“{finding.detail}”</p></div></div></div>}</article>;
}
function IngredientComparison() {
  return <div className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-1441" data-mp="2gemrs4vrruuz">
      <div className="rounded-lg border border-[#e0e7dd] bg-white p-3" data-id="element-1442" data-mp="22dk79wkfasm6">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#748477]" data-id="element-1443" data-mp="22fn3iyc65hzc">Formulated</p>
        <ul className="mt-2 space-y-1.5" data-id="element-1444" data-mp="m71746pyeoj">
          {formulatedIngredients.map(ingredient => <li key={ingredient.name} className={`flex flex-wrap items-baseline justify-between gap-2 text-sm ${ingredient.missing ? 'text-red-700' : 'text-[#56685c]'}`} data-id="element-1445" data-mp="5kqp3a4vi0jj">
              <span className={ingredient.missing ? 'font-semibold' : ''} data-id="element-1446" data-mp="fmuppy3zbldz">{ingredient.name}</span>
              <span className="font-mono text-xs" data-id="element-1447" data-mp="anriq84fv53d">{ingredient.missing ? 'not declared' : ingredient.amount}</span>
            </li>)}
        </ul>
      </div>
      <div className="rounded-lg border border-[#e0e7dd] bg-white p-3" data-id="element-1448" data-mp="jphhy04kb8bp">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#748477]" data-id="element-1449" data-mp="yex7xdw9r9qc">Declared on pack</p>
        <ul className="mt-2 space-y-1.5" data-id="element-1450" data-mp="tfalqeygqj5e">
          {declaredIngredients.map(ingredient => <li key={ingredient} className="text-sm text-[#56685c]" data-id="element-1451" data-mp="17f3juvcbk1ne">{ingredient}</li>)}
        </ul>
      </div>
    </div>;
}
function ProductDataReport() {
  return <div className="grid gap-5 xl:grid-cols-3" data-id="element-1452" data-mp="39783zknktunk"><div className="space-y-5" data-id="element-1453" data-mp="3989lo7z3lfe9"><section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" data-id="element-1454" data-mp="399knw3cwaf39"><div className="border-b border-[#e5ebe3] px-5 py-4" data-id="element-1455" data-mp="357pox5ifsdsn"><h2 className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-1456" data-mp="1861xdv65vz7c">Supplement facts</h2></div><table className="w-full text-left text-sm" data-id="element-1457" data-mp="1d3tpap6t3lbj"><thead className="bg-[#fbfdf9] font-mono text-[10px] uppercase tracking-[0.12em] text-[#748477]" data-id="element-1458" data-mp="1d4lla14bfl2c"><tr data-id="element-1459" data-mp="1i1v9jdfyxqol"><th className="px-5 py-3" data-id="element-1460" data-mp="1i24xywld7ps6">Nutrient</th><th className="px-3 py-3" data-id="element-1461" data-mp="1n29gst9qwwlk">Per sachet</th><th className="px-5 py-3 text-right" data-id="element-1462" data-mp="1n3at112hzp4v">Per 100 g</th></tr></thead><tbody className="divide-y divide-[#edf0eb]" data-id="element-1463" data-mp="1r4e65ft5v72e">{supplementFacts.map(row => <tr key={row.nutrient} data-id="element-1464" data-mp="1r2a6ja2aou7x"><td className="px-5 py-3.5 font-semibold text-evergreen" data-id="element-1465" data-mp="1w2xv7p31wyyz"><span className="flex items-center gap-1.5" data-id="element-1466" data-mp="1w1eejxdhglwo">{row.nutrient}{row.flag && row.flagNote && <Tooltip content={row.flagNote} data-id="element-1467" data-mp="20v8tyom22epy"><Flag className={`h-3.5 w-3.5 ${row.flag === 'red' ? 'text-red-600' : 'text-amber-600'}`} data-id="element-1468" data-mp="20w0p5ybqqafp" /></Tooltip>}</span></td><td className="px-3 py-3.5 text-[#617366]" data-id="element-1469" data-mp="2as3u1bxz8n6c">{row.perSachet}</td><td className="px-5 py-3.5 text-right text-[#617366]" data-id="element-1470" data-mp="2atool1kak647">{row.per100g}</td></tr>)}</tbody></table></section><section className="rounded-2xl border border-[#dce3da] bg-white p-5 shadow-sm" data-id="element-1471" data-mp="2etyt09sfqogy"><div className="flex gap-3" data-id="element-1472" data-mp="3751wq0o28wy"><span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#eff3ed] text-forest-green" data-id="element-1473" data-mp="3492syd31e6x"><Leaf className="h-4 w-4" data-id="element-1474" data-mp="3vc8xqhqu03bl" /></span><div data-id="element-1475" data-mp="3qat96fpg3eog"><h2 className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-1476" data-mp="3qajjkt8bb9ja">Ingredients</h2><p className="text-sm text-[#718174]" data-id="element-1477" data-mp="3lbykix4sqvwf">8 components, 2 additives</p></div></div><p className="mt-5 text-sm italic leading-relaxed text-[#617366]" data-id="element-1478" data-mp="3le2y66shz99w">ORANGE JUICE POWDER (ORANGE JUICE, SOLUBLE CORN FIBER, MANDARIN ORANGE JUICE, NATURAL FLAVOR, SILICON DIOXIDE [ANTICAKING]), SOLUBLE CORN FIBER, SILICON DIOXIDE (ANTICAKING), STEVIA EXTRACT, BETA CAROTENE (COLOR).</p><p className="mt-5 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#718174]" data-id="element-1479" data-mp="d2yn0xsgbaqc">Components</p><ul className="mt-2 divide-y divide-[#edf0eb]" data-id="element-1480" data-mp="83c9en8pnsh0">{ingredientComponents.map(([name, percentage]) => <li key={name} className="flex justify-between gap-4 py-2.5 text-sm" data-id="element-1481" data-mp="15m5aumoj17kv"><span className="text-evergreen" data-id="element-1482" data-mp="10t2niwaceud4">{name}</span><span className="font-mono text-xs text-[#718174]" data-id="element-1483" data-mp="10satvr0b36ph">{percentage}</span></li>)}</ul></section></div><section className="space-y-4" data-id="element-1484" data-mp="94pwudt347xb">{claimCards.map(claim => <article key={claim.title} className="rounded-2xl border border-[#dce3da] bg-white p-5 shadow-sm" data-id="element-1485" data-mp="dyav86ylb3x1"><h2 className="font-[Figtree] text-sm font-semibold text-evergreen" data-id="element-1486" data-mp="6w1p6723kbq">{claim.title}</h2><p className="mt-4 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#718174]" data-id="element-1487" data-mp="46ofbxbb4gny">Context</p><p className="mt-1 text-sm leading-relaxed text-[#617366]" data-id="element-1488" data-mp="ry3kdxugxc2w">{claim.context}</p><p className="mt-4 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#718174]" data-id="element-1489" data-mp="wwn8o4wjazyf">Location</p><p className="mt-1 text-sm font-medium text-evergreen" data-id="element-1490" data-mp="ix5efa0gyuho">{claim.location}</p></article>)}</section><section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" data-id="element-1491" data-mp="nuxewsf6joxh"><div className="border-b border-[#e5ebe3] px-5 py-4" data-id="element-1492" data-mp="33m1ub88ggv2s"><h2 className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-1493" data-mp="33l9wdqrdyl9c">Extracted fields</h2></div><div className="divide-y divide-[#edf0eb]" data-id="element-1494" data-mp="38n8zczj7rg5r">{extractedFields.map(field => <div key={field.label} className="px-5 py-4" data-id="element-1495" data-mp="303m8u8de6b6m"><div className="flex justify-between gap-3" data-id="element-1496" data-mp="306qhbhxw3t33"><p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#718174]" data-id="element-1497" data-mp="2w3tx5mspbox1">{field.label}</p><span className={`font-mono text-[10px] font-semibold uppercase tracking-[0.1em] ${field.confidence === 'High confidence' ? 'text-forest-green' : 'text-[#8d9990]'}`} data-id="element-1498" data-mp="3a1s80i7td37s">{field.confidence}</span></div><p className={`mt-2 text-sm leading-relaxed ${field.value === 'not detected' ? 'italic text-[#8a968c]' : 'text-evergreen'}`} data-id="element-1499" data-mp="2hf7ilnawf16v">{field.value}</p></div>)}</div></section></div>;
}
function DocumentsReport() {
  return <section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" data-id="element-1500" data-mp="2r4r2mwacl13b"><div className="flex flex-col gap-3 border-b border-[#e5ebe3] px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-1501" data-mp="2mc7ony7r03xz"><h2 className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-1502" data-mp="1ym3knpqne26g">Artwork documents</h2><button type="button" className="inline-flex items-center gap-2 self-start rounded-lg bg-forest-green px-3 py-2 text-sm font-semibold text-white hover:bg-evergreen sm:self-auto" data-id="element-1503" data-mp="1tnsatf0crro8"><Plus className="h-4 w-4" data-id="element-1504" data-mp="l63rgzp1zoa8" />Add artwork</button></div><div className="overflow-x-auto" data-id="element-1505" data-mp="l37vhgwrn9of"><table className="w-full min-w-[760px] text-left text-sm" data-id="element-1506" data-mp="ztyogsa3get4"><thead className="border-b border-[#edf0eb] bg-[#fbfdf9] font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#748477]" data-id="element-1507" data-mp="zvjh2cculnoc"><tr data-id="element-1508" data-mp="18umzrf83s12b"><th className="px-5 py-3" data-id="element-1509" data-mp="18un1bjnr494f">Filename</th><th className="px-4 py-3" data-id="element-1510" data-mp="18tllz2zpd0gw">Face</th><th className="px-4 py-3" data-id="element-1511" data-mp="18wq0ouasnelt">Type</th><th className="px-4 py-3" data-id="element-1512" data-mp="13wbr37tjnwlo">Size</th><th className="px-4 py-3" data-id="element-1513" data-mp="13vaev00sl42d">Uploaded</th><th className="px-5 py-3 text-right" data-id="element-1514" data-mp="1it2sfmms3idx">Preview</th></tr></thead><tbody className="divide-y divide-[#edf0eb]" data-id="element-1515" data-mp="1isko02pypxux">{documents.map(([filename, face, type, size, uploaded]) => <tr key={filename} data-id="element-1516" data-mp="1duj45fkppuie"><td className="px-5 py-4 font-medium text-evergreen" data-id="element-1517" data-mp="1drwwzh1qhh0p"><span className="flex items-center gap-2" data-id="element-1518" data-mp="3kjo251z29uk8"><FileText className="h-4 w-4 text-[#819083]" data-id="element-1519" data-mp="3kmjxcijiy54z" />{filename}</span></td><td className="px-4 py-4 text-[#617366]" data-id="element-1520" data-mp="3fmeargs438tr">{face}</td><td className="px-4 py-4 text-[#617366]" data-id="element-1521" data-mp="1v3byql4elz5h">{type}</td><td className="px-4 py-4 text-[#617366]" data-id="element-1522" data-mp="1v4n24jtxt4w2">{size}</td><td className="px-4 py-4 text-[#617366]" data-id="element-1523" data-mp="202f303ck81ce">{uploaded}</td><td className="px-5 py-4 text-right" data-id="element-1524" data-mp="201vlgnqtneyw"><button type="button" className="inline-flex items-center gap-1.5 rounded-lg border border-[#d6dfd3] px-3 py-1.5 text-xs font-semibold text-forest-green hover:bg-[#f7faf5]" data-id="element-1525" data-mp="20484734eltd0"><Eye className="h-3.5 w-3.5" data-id="element-1526" data-mp="1qar7nrcvu05n" />Preview</button></td></tr>)}</tbody></table></div></section>;
}
function RequirementRow({
  requirement
}: {
  requirement: Requirement;
}) {
  const isVoluntary = requirement.status === 'voluntary';
  const isPass = requirement.status === 'pass';
  return <div className={`flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between ${isVoluntary ? 'bg-[#fafbfa] opacity-55' : ''}`} data-id="element-1527" data-mp="12fohng2cdqnl"><div className="flex items-start gap-3" data-id="element-1528" data-mp="17fkn7jqotq59">{isPass ? <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-forest-green text-white" data-id="element-1529" data-mp="2wtpg3ztzcbn5"><Check className="h-3 w-3" data-id="element-1530" data-mp="31qpmkbuqrges" /></span> : isVoluntary ? <span className="mt-0.5 h-5 w-5 rounded-full border-2 border-[#a8b3aa]" data-id="element-1531" data-mp="30alkj6vkcggh" /> : <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-100 text-red-700" data-id="element-1532" data-mp="30bwp38wu1s8n"><CircleAlert className="h-3 w-3" data-id="element-1533" data-mp="2vdle2uusxbou" /></span>}<div data-id="element-1534" data-mp="2qbvzj58defw0"><p className="text-sm font-semibold text-evergreen" data-id="element-1535" data-mp="2qbw3thfel21t">{requirement.name}</p>{requirement.note && <p className="mt-1 text-xs font-medium text-red-700" data-id="element-1536" data-mp="2ldui0oqleomn">{requirement.note}</p>}</div></div><span className={`self-start rounded-full px-2.5 py-1 text-xs font-semibold sm:self-auto ${isPass ? 'bg-forest-green/10 text-forest-green' : isVoluntary ? 'bg-slate-100 text-slate-500' : 'bg-red-100 text-red-700'}`} data-id="element-1537" data-mp="3j0tjdcs8ckqv">{isPass ? 'Pass' : isVoluntary ? 'Voluntary' : 'Fail'}</span></div>;
}